import { ApiResponse } from '../schemas/apiResponseSchema';
import apiClient from '../clients/apiClient';
import { Schemas } from '../types/api-types';

type LoginResponse = Schemas['LoginResponse'];
type ProfileResponse = Schemas['AuthResponse'];
export type UserGroupsResponse = Schemas['UserGroupsResponse'];

export interface LoginOtpStartResponse {
    otp_required: boolean;
    otp_token?: string | null;
    phone_masked?: string | null;
    name?: string | null;
    user_name?: string | null;
    dev_otp?: string | null;
}

export interface TsoPhone {
    id: number;
    phone_number: string;
    name?: string | null;
    status?: number | null;
}

export interface VerifyUserNicResponse {
    success: boolean;
    nic?: string | null;
    full_name?: string | null;
    date_of_birth?: string | null;
    gender?: string | null;
    address?: string | null;
    nic_image_path?: string | null;
    is_valid: boolean;
    message?: string | null;
}

export interface AdministrativeDistrict {
    district_code: string;
    district_name: string;
}

export interface AdministrativeDivision {
    id: number;
    code: string;
    name: string;
}

export interface SendOtpResponse {
    success: boolean;
    dev_otp?: string | null;
    message?: string | null;
}

export interface VerifyOtpResponse {
    success: boolean;
    message?: string | null;
}

export type LoginResult = LoginResponse | LoginOtpStartResponse;

function getDeviceId(): string {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
        deviceId = crypto.randomUUID();
        localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
}

export async function login(user_name: string, password: string): Promise<LoginResult> {
    try {
        const response: ApiResponse<LoginResult> = (await apiClient.post('/auth/login', { user_name, password, device_id: getDeviceId() })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Login failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function verifyLoginOtp(otp_token: string, otp_code: string): Promise<LoginResponse> {
    try {
        const response: ApiResponse<LoginResponse> = (await apiClient.post('/auth/verify_login_otp', { otp_token, otp_code })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'OTP verification failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function resendLoginOtp(otp_token: string): Promise<LoginOtpStartResponse> {
    try {
        const response: ApiResponse<LoginOtpStartResponse> = (await apiClient.post('/auth/resend_login_otp', { otp_token, device_id: getDeviceId() })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Resending OTP failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function sendOtp(phone: string, purpose: string): Promise<SendOtpResponse> {
    try {
        const response: ApiResponse<SendOtpResponse> = (await apiClient.post('/users/send_otp', { phone, purpose })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Sending OTP failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function verifyOtp(phone: string, otp_code: string, purpose: string): Promise<VerifyOtpResponse> {
    try {
        const response: ApiResponse<VerifyOtpResponse> = (await apiClient.post('/users/verify_otp', { phone, otp_code, purpose })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'OTP verification failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function verifyNic(nic: string, imageBase64: string, mimeType: string): Promise<VerifyUserNicResponse> {
    try {
        const response: ApiResponse<VerifyUserNicResponse> = (await apiClient.post('/users/verify_nic', {
            nic,
            image: imageBase64,
            mime_type: mimeType
        })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'NIC verification failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getTsoPhones(): Promise<TsoPhone[]> {
    try {
        const response: ApiResponse<TsoPhone[]> = (await apiClient.get('/users/tso_phones')).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching TSO phones failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getAdministrativeDistricts(): Promise<AdministrativeDistrict[]> {
    try {
        const response: ApiResponse<AdministrativeDistrict[]> = (await apiClient.get('/users/administrative_districts')).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching administrative districts failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getAdministrativeDivisions(districtName?: string | null): Promise<AdministrativeDivision[]> {
    try {
        const response: ApiResponse<AdministrativeDivision[]> = (await apiClient.get('/users/administrative_divisions', {
            params: districtName ? { district_name: districtName } : {},
        })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching administrative divisions failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getGroupPermissions(groupId: number): Promise<number[]> {
    const response: ApiResponse<number[]> = (await apiClient.get(`/users/groups/${groupId}/permissions`)).data;
    return response.data;
}

export async function getGroupMenus(groupId: number): Promise<number[]> {
    const response: ApiResponse<number[]> = (await apiClient.get(`/users/groups/${groupId}/menus`)).data;
    return response.data;
}

export async function getGroupReports(groupId: number): Promise<number[]> {
    const response: ApiResponse<number[]> = (await apiClient.get(`/users/groups/${groupId}/reports`)).data;
    return response.data;
}

export async function logout() {
    try {
        await apiClient.post('/auth/logout');
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Logout failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function getProfile(): Promise<ProfileResponse> {
    try {
        const response: ApiResponse<ProfileResponse> = (await apiClient.get('/profile/me')).data;
        return response.data;
    } catch (error: any) {
        throw error;
    }
}

export async function getAllUserGroups(): Promise<UserGroupsResponse[]> {
    try {
        const response: ApiResponse<UserGroupsResponse[]> = (await apiClient.get('/users/groups/all')).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'Fetching user groups failed');
        }
        throw new Error('network error, please contact support team');
    }
}

export async function firstTimeLogin(user_id: number, user_name: string, updated_user_name: string, password: string, token: string): Promise<LoginResponse> {
    try {
        const response: ApiResponse<LoginResponse> = (await apiClient.post('/auth/first_time_login', {
            user_id,
            user_name,
            updated_user_name,
            password,
            token
        })).data;
        return response.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || 'First time login failed');
        }
        throw new Error('network error, please contact support team');
    }
}